<template>
  <z-button
    link
    type="primary"
    :size="size"
    :class="ns.e('upload-cell-trigger')"
    @click.stop="openDialog"
  >
    {{ t('z.table.uploadCell.upload') }}
  </z-button>

  <z-dialog
    v-model="dialogVisible"
    :title="dialogTitle"
    width="560px"
    append-to-body
    align-center
    :class="ns.e('upload-dialog')"
    @closed="onDialogClosed"
  >
    <z-upload
      v-model:file-list="draftFileList"
      drag
      :action="uploadAction"
      :data="uploadData"
      :headers="uploadHeaders"
      :http-request="gridFileUploadRequest"
      list-type="text"
      :limit="uploadLimit"
      :multiple="isMultipleMode"
      :accept="accept"
      @success="onUploadSuccess"
      @error="onUploadError"
      @remove="onUploadRemove"
    >
      <div :class="ns.e('upload-dragger-tip')">
        <p>{{ t('z.table.uploadCell.uploadDrag') }}</p>
        <p v-if="accept" :class="ns.e('upload-dragger-hint')">
          {{ t('z.table.uploadCell.uploadAcceptHint', { accept }) }}
        </p>
      </div>
    </z-upload>

    <template #footer>
      <z-button @click="cancelDialog">
        {{ t('z.messagebox.cancel') }}
      </z-button>
      <z-button type="primary" @click="confirmDialog">
        {{ t('z.messagebox.confirm') }}
      </z-button>
    </template>
  </z-dialog>
</template>

<script setup lang="ts">
import { computed, inject, ref } from 'vue'
import { useGlobalConfig } from '@zoryn/components/config-provider'
import ZButton from '@zoryn/components/button'
import ZDialog from '@zoryn/components/dialog'
import { ZMessage } from '@zoryn/components/message'
import ZUpload, { ajaxUpload } from '@zoryn/components/upload'
import { useLocale, useNamespace } from '@zoryn/hooks'
import {
  getAccessToken,
  getCommonResultMessage,
  isCommonResultSuccess,
} from '@zoryn/network'
import {
  buildFilesCellMeta,
  createUploadFilesFromFileMeta,
  createUploadFilesFromFileMetaList,
  resolveFileCellMetaList,
  resolveUploadResponseFileMeta,
  serializeFileCellValue,
  serializeFilesCellValue,
  syncFileCellMeta,
  type GridFileCellMeta,
} from '../utils/file-cell'
import {
  applyUploadPathWithFilename,
  resolveColumnUploadAction,
  resolveColumnUploadData,
} from '../utils/resolve-column-upload'
import { beginRowEdit } from '../methods/edit'

import type {
  UploadFile,
  UploadRequestHandler,
  UploadUserFile,
} from '@zoryn/components/upload'
import type { ComponentSize } from '@zoryn/constants'
import type { DataGrid, GridColumnInfo } from '../types'

const props = defineProps<{
  row: Record<string, unknown>
  column: GridColumnInfo
  size?: ComponentSize
}>()

const emit = defineEmits<{
  'update:value': [value: unknown]
}>()

const ns = useNamespace('grid')
const { t } = useLocale()
const $grid = inject('$grid') as DataGrid
const gridGlobalConfig = useGlobalConfig('grid')

const dialogVisible = ref(false)
const draftFileList = ref<UploadUserFile[]>([])
const draftMeta = ref<GridFileCellMeta | null>(null)
const draftMetaMap = ref<Record<string, GridFileCellMeta>>({})

const fieldValue = computed(() => props.row[props.column.field!])
const fileMeta = computed(() => {
  const metaKey = `${props.column.field}Meta`
  const meta = props.row[metaKey]
  return isSingleFileMeta(meta) ? meta : null
})

const isMultipleMode = computed(
  () => props.column.type === 'files' || (props.column.uploadLimit ?? 1) > 1
)
const uploadAction = computed(() =>
  resolveColumnUploadAction(props.column, $grid.options, gridGlobalConfig.value)
)
const uploadData = computed(() =>
  resolveColumnUploadData(props.column, props.row)
)
const uploadLimit = computed(
  () => props.column.uploadLimit ?? (isMultipleMode.value ? 10 : 1)
)
const accept = computed(() => props.column.uploadAccept || '')

const uploadHeaders = computed(() => {
  if (props.column.uploadHeaders) {
    return props.column.uploadHeaders
  }
  const token = getAccessToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
})

const gridFileUploadRequest: UploadRequestHandler = (option) => {
  const data = applyUploadPathWithFilename(
    option.data ? { ...(option.data as Record<string, unknown>) } : undefined,
    option.file?.name
  )
  return ajaxUpload({ ...option, data })
}

const dialogTitle = computed(() =>
  t('z.table.uploadCell.fileTitle', {
    title: props.column.title ?? '',
  })
)

function isSingleFileMeta(value: unknown): value is GridFileCellMeta {
  if (!value || typeof value !== 'object') return false
  if ('files' in (value as { files?: unknown })) return false
  return true
}

function openDialog() {
  if (!beginRowEdit($grid, props.row)) {
    return
  }

  if (!uploadAction.value) {
    ZMessage.warning(t('z.table.uploadCell.missingUploadAction'))
    return
  }

  draftMetaMap.value = {}

  if (isMultipleMode.value) {
    const files = resolveFileCellMetaList(
      props.row,
      props.column.field!,
      props.column.type
    )
    draftFileList.value = createUploadFilesFromFileMetaList(files)
    for (let index = 0; index < draftFileList.value.length; index += 1) {
      const uploadFile = draftFileList.value[index]!
      const meta = files[index]
      if (meta) {
        draftMetaMap.value[uploadFile.uid] = meta
      }
    }
  } else {
    draftMeta.value = fileMeta.value
    draftFileList.value = createUploadFilesFromFileMeta(
      fieldValue.value,
      fileMeta.value
    )
  }

  dialogVisible.value = true
}

function onUploadSuccess(response: unknown, uploadFile: UploadFile) {
  if (!isCommonResultSuccess(response)) {
    uploadFile.status = 'fail'
    ZMessage.error(getCommonResultMessage(response))
    return
  }

  const meta = resolveUploadResponseFileMeta(response)
  if (!meta?.id && !meta?.url) {
    uploadFile.status = 'fail'
    ZMessage.error(t('z.table.uploadCell.uploadFailed'))
    return
  }

  const resolvedMeta = {
    ...meta,
    name: meta?.name || uploadFile.name,
  }

  if (isMultipleMode.value) {
    draftMetaMap.value[uploadFile.uid] = resolvedMeta
  } else {
    draftMeta.value = resolvedMeta
  }

  if (meta.url) {
    uploadFile.url = meta.url
  }
}

function onUploadRemove(uploadFile: UploadFile) {
  delete draftMetaMap.value[uploadFile.uid]
}

function onUploadError(error: Error) {
  ZMessage.error(error?.message || t('z.table.uploadCell.uploadFailed'))
}

function cancelDialog() {
  dialogVisible.value = false
}

function collectMultipleMetas(): GridFileCellMeta[] {
  return draftFileList.value
    .filter((file) => file.status === 'success')
    .map((file) => {
      const mapped = draftMetaMap.value[file.uid]
      if (mapped) return mapped
      return {
        name: file.name,
        url: file.url,
      }
    })
    .filter((meta) => meta.id != null || meta.name || meta.url)
}

function confirmDialog() {
  if (draftFileList.value.some((file) => file.status === 'uploading')) {
    ZMessage.warning(t('z.table.uploadCell.uploading'))
    return
  }

  const pendingUpload = draftFileList.value.filter(
    (file) => file.raw && file.status !== 'success'
  )
  if (pendingUpload.length > 0) {
    ZMessage.warning(t('z.table.uploadCell.uploadFailed'))
    return
  }

  if (draftFileList.value.length === 0) {
    emit('update:value', null)
    if (props.column.field) {
      syncFileCellMeta(props.row, props.column.field, null)
    }
    dialogVisible.value = false
    return
  }

  if (isMultipleMode.value) {
    const metas = collectMultipleMetas()
    if (!metas.length) {
      ZMessage.warning(t('z.table.uploadCell.uploadFailed'))
      return
    }

    emit('update:value', serializeFilesCellValue(metas))
    if (props.column.field) {
      const filesMeta = buildFilesCellMeta(metas)
      if (filesMeta) {
        syncFileCellMeta(props.row, props.column.field, filesMeta)
      }
    }
    dialogVisible.value = false
    return
  }

  if (!draftMeta.value?.id && !draftMeta.value?.url) {
    ZMessage.warning(t('z.table.uploadCell.uploadFailed'))
    return
  }

  const value = serializeFileCellValue(draftMeta.value)
  emit('update:value', value)
  if (props.column.field) {
    syncFileCellMeta(props.row, props.column.field, draftMeta.value)
  }
  dialogVisible.value = false
}

function onDialogClosed() {
  draftFileList.value = []
  draftMeta.value = null
  draftMetaMap.value = {}
}
</script>
